/**
 * useToast — Lightweight toast notification system.
 *
 * Usage:
 *   // Wrap app with ToastProvider:
 *   <ToastProvider><App /></ToastProvider>
 *
 *   // In any component:
 *   const { showToast } = useToast()
 *   showToast('Copied to clipboard', 'success')
 *   showToast('Failed to save', 'error', 5000)
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react'

const ToastContext = createContext(null)

const MAX_TOASTS = 3
const DEFAULT_DURATION = 3000

const TYPE_CONFIG = {
  success: { icon: CheckCircle2, bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', iconColor: 'text-emerald-500' },
  error: { icon: XCircle, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', iconColor: 'text-red-500' },
  warning: { icon: AlertTriangle, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', iconColor: 'text-amber-500' },
  info: { icon: Info, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', iconColor: 'text-blue-500' },
}

function Toast({ id, message, type, onDismiss }) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.info
  const Icon = config.icon

  return (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-lg border shadow-lg ${config.bg} ${config.border} animate-fade-in max-w-sm`}
      role="alert"
    >
      <Icon size={16} className={config.iconColor} />
      <span className={`text-sm font-medium flex-1 ${config.text}`}>{message}</span>
      <button
        onClick={() => onDismiss(id)}
        className="p-0.5 rounded hover:bg-black/5 transition-colors"
      >
        <X size={14} className="text-gray-400" />
      </button>
    </div>
  )
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const timersRef = useRef({})
  const idRef = useRef(0)

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id])
      delete timersRef.current[id]
    }
  }, [])

  const showToast = useCallback((message, type = 'info', duration = DEFAULT_DURATION) => {
    const id = ++idRef.current

    setToasts(prev => {
      // Keep only the most recent toasts
      const next = [...prev, { id, message, type }]
      return next.slice(-MAX_TOASTS)
    })

    // Auto-dismiss
    timersRef.current[id] = setTimeout(() => dismiss(id), duration)

    return id
  }, [dismiss])

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout)
    }
  }, [])

  return (
    <ToastContext.Provider value={{ showToast, dismiss }}>
      {children}
      {/* Toast container — bottom-right, stacked */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-auto">
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              id={toast.id}
              message={toast.message}
              type={toast.type}
              onDismiss={dismiss}
            />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  )
}

export default function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
